import moment from "moment-timezone";
import React, { createContext, useContext, useMemo } from "react";
import { z } from "zod";

import { CURRENT_EVENT_SLUG } from "src/shared/constants/event";
import {
  HACKER_POST_RSVP_TOOLS_META_KEY,
  HARDWARE_META_KEY,
  TRAVEL_SUPPORT_BOT_FLAG_KEY,
} from "src/shared/constants/featureFlags";
import { useUserContext } from "src/shared/contexts/UserContext";
import {
  HackerPostRsvpToolsConfig,
  parseHackerPostRsvpTools,
} from "src/shared/utils/hackerPostRsvpAccess";
import { parseHardware } from "src/shared/utils/hardwareAccess";
import { MentorCafeConfig, parseMentorCafe } from "src/shared/utils/mentorCafe";
import { parseTravelSupportBot } from "src/shared/utils/travelSupportAccess";

import { useGetEventMetaQuery } from "./graphql/getEventMeta.generated";

const hackerDirectorySchema = z.object({
  directoryEnabled: z.boolean().catch(false),
  slackLinkingEnabled: z.boolean().catch(false),
  organizerBypass: z.boolean().catch(false),
});

const defaultHackerDirectory = {
  directoryEnabled: false,
  slackLinkingEnabled: false,
  organizerBypass: false,
};

const deadlineSchema = z
  .string()
  .min(1)
  .refine((value) => !Number.isNaN(Date.parse(value)), "Invalid deadline");

const rsvpSchema = z.object({
  isOpen: z.boolean().catch(false),
  isTravelPlanOpen: z.boolean().catch(false),
  organizerBypass: z.boolean().catch(false),
  R1Deadline: deadlineSchema,
  R2Deadline: deadlineSchema,
  R2DeadlineDisplayStartDate: z
    .string()
    .refine(
      (value) =>
        moment.tz(value, "YYYY-MM-DD", true, "America/Toronto").isValid(),
      "Invalid R2 deadline display start date"
    ),
});

export type RsvpConfig = z.infer<typeof rsvpSchema>;

const defaultRsvp: RsvpConfig = {
  isOpen: false,
  isTravelPlanOpen: false,
  organizerBypass: false,
  R1Deadline: "",
  R2Deadline: "",
  R2DeadlineDisplayStartDate: "",
};

const defaultTravelSupportBot = parseTravelSupportBot(undefined);
const defaultHardware = parseHardware(undefined);
const defaultHackerPostRsvpTools = parseHackerPostRsvpTools(undefined);

const judgingSchema = z.object({
  isOpen: z.boolean().catch(false),
  R1StartTime: z.string().nullish().catch(null),
  R2StartTime: z.string().nullish().catch(null),
  draftDeadline: z.string().nullish().catch(null),
  finalDeadline: z.string().nullish().catch(null),
  bannerVisible: z.boolean().catch(false),
});

export type JudgingConfig = z.infer<typeof judgingSchema>;

const defaultJudging: JudgingConfig = {
  isOpen: false,
  R1StartTime: null,
  R2StartTime: null,
  draftDeadline: null,
  finalDeadline: null,
  bannerVisible: false,
};

export const parseJudging = (value: unknown): JudgingConfig => {
  const result = judgingSchema.safeParse(value);
  return result.success ? result.data : defaultJudging;
};

export const parseHackerDirectory = (value: unknown, isOrganizer = false) => {
  const result = hackerDirectorySchema.safeParse(value);
  const hackerDirectory = result.success ? result.data : defaultHackerDirectory;
  const bypassEnabled = isOrganizer && hackerDirectory.organizerBypass;

  return {
    ...hackerDirectory,
    directoryEnabled: hackerDirectory.directoryEnabled || bypassEnabled,
    slackLinkingEnabled: hackerDirectory.slackLinkingEnabled || bypassEnabled,
  };
};

export const parseRsvp = (value: unknown, isOrganizer = false) => {
  const result = rsvpSchema.safeParse(value);
  const rsvp = result.success ? result.data : defaultRsvp;
  const organizerBypassEnabled = isOrganizer && rsvp.organizerBypass;

  return {
    ...rsvp,
    isOpen: rsvp.isOpen || organizerBypassEnabled,
    isTravelPlanOpen: rsvp.isTravelPlanOpen || organizerBypassEnabled,
  };
};

interface EventContextState {
  mentorCafe: MentorCafeConfig;
  hackerDirectory: {
    directoryEnabled: boolean;
    slackLinkingEnabled: boolean;
    organizerBypass: boolean;
  };
  rsvp: RsvpConfig;
  travelSupportBot: {
    enabled: boolean;
  };
  judging: JudgingConfig;
  hardware: {
    isOpen: boolean;
    takingRequests: boolean;
    volunteersWithAccess: string[];
  };
  hackerPostRsvpTools: HackerPostRsvpToolsConfig;
  isLoading: boolean;
}

const EventContext = createContext<EventContextState>({
  mentorCafe: parseMentorCafe(undefined),
  hackerDirectory: defaultHackerDirectory,
  rsvp: defaultRsvp,
  travelSupportBot: defaultTravelSupportBot,
  judging: defaultJudging,
  hardware: defaultHardware,
  hackerPostRsvpTools: defaultHackerPostRsvpTools,
  isLoading: true,
});

export const useEventContext = () => useContext(EventContext);

export const EventContextProvider: React.FC = ({ children }) => {
  const { isAuthenticated, isOrganizer } = useUserContext();
  const { data, loading } = useGetEventMetaQuery({
    variables: { slug: CURRENT_EVENT_SLUG },
    skip: !isAuthenticated,
  });

  const hackerDirectory = useMemo(() => {
    return parseHackerDirectory(data?.eventMeta?.hackerDirectory, isOrganizer);
  }, [data, isOrganizer]);
  const travelSupportBot = useMemo(
    () => parseTravelSupportBot(data?.eventMeta?.[TRAVEL_SUPPORT_BOT_FLAG_KEY]),
    [data]
  );
  const hardware = useMemo(
    () => parseHardware(data?.eventMeta?.[HARDWARE_META_KEY]),
    [data]
  );
  const hackerPostRsvpTools = useMemo(
    () =>
      parseHackerPostRsvpTools(
        data?.eventMeta?.[HACKER_POST_RSVP_TOOLS_META_KEY]
      ),
    [data]
  );

  const rsvp = useMemo(() => {
    if (!isAuthenticated || loading) return defaultRsvp;
    return parseRsvp(data?.eventMeta?.rsvp, isOrganizer);
  }, [data, isAuthenticated, isOrganizer, loading]);

  const judging = useMemo(() => parseJudging(data?.eventMeta?.judging), [data]);
  const mentorCafe = useMemo(
    () => parseMentorCafe(data?.eventMeta?.mentorCafe),
    [data]
  );

  return (
    <EventContext.Provider
      value={{
        mentorCafe,
        hackerDirectory,
        rsvp,
        travelSupportBot,
        judging,
        hardware,
        hackerPostRsvpTools,
        isLoading: isAuthenticated && loading,
      }}
    >
      {children}
    </EventContext.Provider>
  );
};
